/**
 * Boundary rows + drift — V103-4-3 / ADR-0066 §7, §8 (amendments A1, A2).
 *
 *   - `getBoundaryRows` is the single read model every boundary output renders
 *     from (GovRAMP sheets 7 / 8 / 11, FedRAMP MAS). Its `rows_sha256` is the
 *     sha256 of the same canonical content the Prepare → Audit snapshot hashes
 *     (`buildBoundaryPeriodSnapshot`), so a planned period's live hash equals the
 *     hash frozen from it. A period that has left `planned` and has a snapshot is
 *     served FROM the snapshot (later component edits don't change it).
 *     Connector interconnections are derived at read time (ADR-0053 §5) and are
 *     not part of the hash; neither is the drift report.
 *   - `computeBoundaryDrift` is computed on every read and never stored (§7). It
 *     never edits either side: the asset register, the components and the
 *     external services are only read.
 *
 * Every query filters by the caller's workspace id.
 */
import { effectiveCadenceHours } from '../connectors/cadence.js';
import { getConnectorManifest } from '../connectors/manifests.js';
import { programHas } from '../programs/manifests.js';
import { categoryTreatment } from './asset-service.js';
import { BoundaryError, getBoundary, listSystemFacts } from './boundary-service.js';
import { buildBoundaryPeriodSnapshot, getBoundaryPeriodSnapshot, listBoundaryInterconnections, listScopedComponents, } from './boundary-component-service.js';
import { listConnectorInterconnections } from './connector-interconnection-service.js';
/** GovRAMP's program key (ADR-0064; the manifest lands with Track 3). */
export const GOVRAMP_PROGRAM_KEY = 'govramp';
/** FedRAMP classes whose MAS includes MAS-CSO-TPR (FRC-CLA-MFR pulls only IIR into Class A). */
const TPR_CLASSES = new Set(['B', 'C', 'D']);
// ---- drift -----------------------------------------------------------------------------
export const BOUNDARY_DRIFT_KINDS = [
    'asset_retired', 'asset_unscoped', 'asset_excluded', 'asset_recategorized',
    'system_left_boundary', 'provider_retired', 'not_observed',
];
export const BOUNDARY_GAP_KINDS = [
    'federal_data_unanswered', 'missing_inventory_fields', 'tpr_incomplete', 'govramp_subprocessor_unverified',
];
/**
 * Workbook columns a component must fill, beyond the typed `function` and
 * `component_key` (ADR-0066 §8 sheet `11_Inventory Workbook`). Virtual and
 * Public are typed columns; the rest are `attributes`. An inherited component
 * (`inherited_from_service_id`) renders its OS / hardware / patch / scan
 * columns as "Inherited: <provider>", so those are not required of it.
 */
export function requiredInventoryFields(c) {
    const out = ['virtual', 'public', 'location', 'asset_type'];
    const hostLike = c.kind === 'infrastructure' || c.kind === 'container';
    if (hostLike && !c.inherited_from_service_id)
        out.push('os_name_and_version', 'baseline_configuration_name');
    if (c.kind === 'software' || c.kind === 'database' || c.kind === 'managed_service')
        out.push('software_database_vendor');
    if (c.kind === 'software' || c.kind === 'database')
        out.push('software_database_name_and_version');
    return out;
}
function missingFields(c) {
    const missing = [];
    for (const f of requiredInventoryFields(c)) {
        if (f === 'virtual') {
            if (c.is_virtual === null)
                missing.push(f);
            continue;
        }
        if (f === 'public') {
            if (c.is_public === null)
                missing.push(f);
            continue;
        }
        if (!c.attributes[f])
            missing.push(f);
    }
    // One address is required of a non-inherited host: IP or DNS name.
    const hostLike = c.kind === 'infrastructure' || c.kind === 'container';
    if (hostLike && !c.inherited_from_service_id && !c.attributes.ip_address && !c.attributes.dns_name_or_url)
        missing.push('ip_address_or_dns_name');
    return missing;
}
const TPR = ['tpr_usage', 'tpr_justification', 'tpr_mitigations', 'compensating_controls'];
const byKey = (a, b) => (a.component_key ?? '') < (b.component_key ?? '') ? -1 : (a.component_key ?? '') > (b.component_key ?? '') ? 1
    : (a.external_service_id ?? '') < (b.external_service_id ?? '') ? -1 : (a.external_service_id ?? '') > (b.external_service_id ?? '') ? 1 : 0;
const ordered = (items, kinds) => items.sort((a, b) => kinds.indexOf(a.kind) - kinds.indexOf(b.kind) || byKey(a, b));
const DAY_MS = 86_400_000;
/**
 * The drift and completeness report for one program × period (null = standing).
 * Rules: ADR-0066 §7 (+ the GovRAMP §2.4 Provisional cap and MAS-CSO-TPR gaps).
 */
export async function computeBoundaryDrift(db, workspaceId, profileId, periodId, now = new Date()) {
    const prof = (await db.query(`SELECT program, certification_class FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? AND status = 'active' LIMIT 1`, [profileId, workspaceId])).results[0];
    if (!prof)
        throw new BoundaryError('not_found', 'Program not found.');
    const scoped = await listScopedComponents(db, workspaceId, profileId, periodId);
    const comps = scoped.components;
    const drift = [];
    const gaps = [];
    const ref = (c) => ({ component_id: c.component_id, component_key: c.component_key });
    // Assets (read-only link; ADR-0066 §9: nothing here writes assets).
    const assets = new Map((await db.query(`SELECT asset_id, retired_at, updated_at FROM assets WHERE workspace_id = ?`, [workspaceId])).results.map((a) => [a.asset_id, a]));
    const memberships = (await db.query(`SELECT m.asset_id, m.category, m.decided_at, m.removed_at, a.status AS period_status, c.program
       FROM asset_period_memberships m
       JOIN audit_periods a ON a.period_id = m.period_id AND a.workspace_id = m.workspace_id
       JOIN certification_profiles c ON c.profile_id = m.profile_id AND c.workspace_id = m.workspace_id
      WHERE m.workspace_id = ?`, [workspaceId])).results;
    for (const c of comps) {
        if (!c.asset_id)
            continue;
        const a = assets.get(c.asset_id);
        const linked = c.asset_linked_at ?? '';
        if (!a || a.retired_at) {
            drift.push({ kind: 'asset_retired', ...ref(c), asset_id: c.asset_id, detail: 'The linked asset was retired.' });
            continue;
        }
        const mine = memberships.filter((m) => m.asset_id === c.asset_id);
        const live = mine.filter((m) => !m.removed_at && m.period_status !== 'closed');
        if (!live.length)
            drift.push({ kind: 'asset_unscoped', ...ref(c), asset_id: c.asset_id, detail: 'The linked asset is in no open period\'s scope.' });
        if (live.some((m) => categoryTreatment(m.program, m.category) === 'excluded')) {
            drift.push({ kind: 'asset_excluded', ...ref(c), asset_id: c.asset_id, detail: 'The linked asset is categorized out of scope while the component is in this boundary.' });
        }
        if (a.updated_at > linked || mine.some((m) => m.decided_at > linked || (m.removed_at ?? '') > linked)) {
            drift.push({ kind: 'asset_recategorized', ...ref(c), asset_id: c.asset_id, detail: 'The linked asset changed after it was linked; re-confirm the link.' });
        }
    }
    // Systems.
    const inBoundary = new Set((await getBoundary(db, workspaceId, profileId, periodId)).systems.map((s) => s.system_id));
    for (const c of comps) {
        if (c.system_id && !inBoundary.has(c.system_id)) {
            drift.push({ kind: 'system_left_boundary', ...ref(c), system_id: c.system_id, detail: 'The component\'s system is no longer in this boundary.' });
        }
    }
    // Providers (external services) the boundary references.
    const services = new Map((await db.query(`SELECT external_service_id, name, retired_at, fedramp_status, govramp_status, govramp_status_checked_at, tpr_usage, tpr_justification, tpr_mitigations, compensating_controls
       FROM external_services WHERE workspace_id = ?`, [workspaceId])).results.map((s) => [s.external_service_id, s]));
    const ids = new Set(comps.map((c) => c.component_id));
    const interconnections = (await listBoundaryInterconnections(db, workspaceId)).filter((i) => ids.has(i.component_id));
    const referenced = new Set();
    for (const c of comps) {
        const pointers = [c.external_service_id, c.inherited_from_service_id, ...interconnections.filter((i) => i.component_id === c.component_id).map((i) => i.external_service_id)]
            .filter((x) => !!x);
        for (const sid of new Set(pointers)) {
            referenced.add(sid);
            const s = services.get(sid);
            if (s?.retired_at)
                drift.push({ kind: 'provider_retired', ...ref(c), external_service_id: sid, detail: `${s.name} was retired from the register.` });
        }
    }
    // Not observed: a collector-sourced component unseen for more than two schedule intervals after successful runs.
    const connectors = new Map((await db.query(`SELECT connector_id, kind, status, config_json, last_run_at, consecutive_failures FROM evidence_connectors WHERE workspace_id = ?`, [workspaceId])).results
        .map((x) => [x.connector_id, x]));
    for (const c of comps) {
        if (!c.source_connector_id)
            continue;
        const conn = connectors.get(c.source_connector_id);
        if (!conn || conn.status !== 'active' || !conn.last_run_at || (conn.consecutive_failures ?? 0) > 0)
            continue;
        const windowMs = 2 * effectiveCadenceHours(getConnectorManifest(conn.kind), conn.config_json) * 3_600_000;
        const seen = c.last_observed_at ?? c.added_at;
        if (Date.parse(conn.last_run_at) - Date.parse(seen) > windowMs) {
            const days = Math.floor((now.getTime() - Date.parse(seen)) / DAY_MS);
            drift.push({ kind: 'not_observed', ...ref(c), connector_id: conn.connector_id, days_unobserved: days, detail: `Not listed by its connector for ${days} day(s).` });
        }
    }
    // Completeness gaps.
    for (const c of comps) {
        if (c.federal_data === null)
            gaps.push({ kind: 'federal_data_unanswered', ...ref(c), detail: 'Say whether the component handles or impacts federal data.' });
        const missing = missingFields(c);
        if (missing.length)
            gaps.push({ kind: 'missing_inventory_fields', ...ref(c), fields: missing, detail: `Inventory workbook fields to fill: ${missing.join(', ')}.` });
    }
    const live = [...referenced].map((id) => services.get(id)).filter((s) => !!s && !s.retired_at)
        .sort((a, b) => (a.external_service_id < b.external_service_id ? -1 : 1));
    if (programHas(prof.program, (m) => !!m.features.persistentValidation) && TPR_CLASSES.has(prof.certification_class ?? '')) {
        for (const s of live) {
            const empty = TPR.filter((f) => !s[f]);
            if (empty.length)
                gaps.push({ kind: 'tpr_incomplete', component_id: null, component_key: null, external_service_id: s.external_service_id, fields: [...empty], detail: `${s.name}: third-party resource fields to fill: ${empty.join(', ')}.` });
        }
    }
    if (prof.program === GOVRAMP_PROGRAM_KEY) {
        for (const s of live) {
            const govramp = s.govramp_status === 'authorized' && !!s.govramp_status_checked_at;
            if (govramp || s.fedramp_status === 'authorized')
                continue;
            gaps.push({
                kind: 'govramp_subprocessor_unverified', component_id: null, component_key: null, external_service_id: s.external_service_id, cap: 'provisionally_authorized',
                detail: `${s.name} is not verified GovRAMP Authorized or FedRAMP authorized; the product would be capped at Provisionally Authorized.`,
            });
        }
    }
    return {
        profile_id: profileId, program: prof.program, period_id: periodId, as_of: now.toISOString(),
        drift: ordered(drift, BOUNDARY_DRIFT_KINDS), gaps: ordered(gaps, BOUNDARY_GAP_KINDS),
    };
}
// ---- rows (renderer contract, §8) ----------------------------------------------------------
export const BOUNDARY_ROWS_FORMAT = 'boundary-rows/v1';
/**
 * The one read model every boundary renderer takes (ADR-0066 §8). A period that
 * has left `planned` is served from its snapshot when it has one.
 */
export async function getBoundaryRows(db, workspaceId, profileId, periodId, now = new Date()) {
    const prof = (await db.query(`SELECT program FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? AND status = 'active' LIMIT 1`, [profileId, workspaceId])).results[0];
    if (!prof)
        throw new BoundaryError('not_found', 'Program not found.');
    let status = null;
    if (periodId) {
        status = (await db.query(`SELECT status FROM audit_periods WHERE period_id = ? AND workspace_id = ? AND profile_id = ? LIMIT 1`, [periodId, workspaceId, profileId])).results[0]?.status ?? null;
        if (!status)
            throw new BoundaryError('not_found', 'Period not found.');
    }
    const snap = periodId && status !== 'planned' ? await getBoundaryPeriodSnapshot(db, workspaceId, periodId) : null;
    let content;
    let rows_sha256;
    if (snap && snap.profile_id === profileId) {
        content = JSON.parse(snap.rows_json);
        rows_sha256 = snap.rows_sha256;
    }
    else {
        const live = await buildBoundaryPeriodSnapshot(db, workspaceId, profileId, periodId);
        content = JSON.parse(live.rows_json);
        rows_sha256 = live.rows_sha256;
    }
    const facts = new Map((await listSystemFacts(db, workspaceId)).map((f) => [f.system_id, f]));
    const components = content.components.map(({ interconnections: _i, ports: _p, ...c }) => c);
    const manual = content.components.flatMap((c) => c.interconnections.map((i) => ({ ...i, component_id: c.component_id, source: 'manual' })));
    const derived = (await listConnectorInterconnections(db, workspaceId)).map((i) => ({ ...i, source: 'connector' }));
    return {
        format: BOUNDARY_ROWS_FORMAT,
        profile_id: profileId,
        program: prof.program,
        period_id: periodId,
        as_of: now.toISOString(),
        source: snap && snap.profile_id === profileId ? 'snapshot' : 'live',
        snapshot_id: snap?.snapshot_id ?? null,
        frozen_at: snap?.frozen_at ?? null,
        rows_sha256,
        scope_source: content.scope_source,
        systems: content.systems.map((id) => {
            const f = facts.get(id);
            return { system_id: id, name: f?.name ?? null, handles_federal_data: f?.handles_federal_data ?? null, internet_reachable: f?.internet_reachable ?? null };
        }),
        components,
        interconnections: [...manual, ...derived],
        ports: content.components.flatMap((c) => c.ports.map((p) => ({ ...p, component_id: c.component_id }))),
        external_services: content.external_services,
        drift: await computeBoundaryDrift(db, workspaceId, profileId, periodId, now),
    };
}
//# sourceMappingURL=boundary-rows-service.js.map